import React, { useEffect, useState, useMemo } from 'react';
import { Button } from 'antd';
import {
  CommentOutlined,
  EditOutlined,
  DeleteOutlined,
  RollbackOutlined,
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import httpInjectorService from 'services/http-injector.service';
import { toast } from 'react-toastify';
import { setSpaceAssigneeList } from 'store/actions';
import UserAvatar from 'components/userAvatar';
import { getColorForUsername } from 'components/utils/AvatarColor';
import Cookies from 'js-cookie';
import ReactQuill from 'react-quill';
import Quill from 'quill';
import Mention from 'quill-mention';
import { shallowEqual } from 'react-redux';

import 'react-quill/dist/quill.snow.css';
import 'quill-mention/dist/quill.mention.css';

Quill.register('modules/mention', Mention);

const PageComments = ({ pageid, spaceid, type = 'page', isPanel = false }) => {
  const dispatch = useDispatch();

  const assigneeOptions = useSelector(
    (state) => state.Authentication?.spaceassignees,
    shallowEqual,
  );

  const [showBox, setShowBox] = useState(false);

  const [comment, setComment] = useState('');
  const [replyText, setReplyText] = useState('');
  const [editText, setEditText] = useState('');

  const [comments, setComments] = useState([]);

  const [replyParent, setReplyParent] = useState(null);
  const [editingId, setEditingId] = useState(null);

  const mentionUsers = useMemo(() => {
    return (
      assigneeOptions?.map((u) => ({
        id: u.user_id,
        value: u.username,
      })) || []
    );
  }, [assigneeOptions]);

  const getSpaceUsers = async () => {
    try {
      const res = await httpInjectorService.getSpaceUser(spaceid);

      if (res.status === 'success') {
        dispatch(setSpaceAssigneeList(res.data));
      }
    } catch (error) {
      console.error(error);
      toast.error('Failed to load users');
    }
  };

  useEffect(() => {
    getSpaceUsers();
    getPageComments();
  }, []);

  const hexToRgb = (hex) => {
    if (!hex) return '';
    hex = hex.replace('#', '');

    const bigint = parseInt(hex, 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;

    return `rgb(${r}, ${g}, ${b})`;
  };

  const quillModules = useMemo(
    () => ({
      toolbar: [
        ['bold', 'italic', 'underline'],
        [{ list: 'bullet' }],
        ['link'],
      ],
      mention: {
        mentionDenotationChars: ['@'],
        source: function (searchTerm, renderList) {
          const values = mentionUsers || [];

          if (searchTerm.length === 0) {
            renderList(values, searchTerm);
          } else {
            const matches = values.filter((item) =>
              item.value.toLowerCase().includes(searchTerm.toLowerCase()),
            );
            renderList(matches, searchTerm);
          }
        },
      },
    }),
    [mentionUsers],
  );

  const MemoQuill = React.memo(ReactQuill);

  const formatDate = (date) => {
    return new Date(date).toLocaleString('en-IN', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  };

  const getInitials = (username) =>
    username
      .split(' ')
      .map((w) => w[0])
      .join('')
      .toUpperCase();

  const extractMentionIds = (html) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    const mentions = [...doc.querySelectorAll('.mention')];

    return mentions.map((m) => Number(m.dataset.id));
  };

  const handleAddComment = async (parentId = null) => {
    const text = parentId ? replyText : comment;
    const username = Cookies.get('username');

    if (!text) return;

    try {
      const assignerHex = getColorForUsername(username);
      const assignerRgb = hexToRgb(assignerHex);

      const mentionedUsers = extractMentionIds(text);

      const payload = {
        space_id: spaceid,
        item_id: pageid,
        parent_comment_id: parentId,
        type: type,
        inline_position: null,
        content: text,
        mentions: mentionedUsers,
        sharePageLink: `${window.location.origin}/admin/confluence/space/${spaceid}/page/${pageid}`,
        shareCommentLink: `${window.location.origin}/admin/confluence/space/${spaceid}/page/${pageid}`,
        assigner_avatar: [
          {
            assigner_avatar_code: getInitials(username),
            assigner_avatar_color_code: assignerRgb,
          },
        ],
      };

      const response = await httpInjectorService.postpageComment(payload);

      if (response?.status === 'success') {
        getPageComments();
      }

      setComment('');
      setReplyText('');
      setReplyParent(null);
      setShowBox(false);
    } catch (error) {
      console.error(error);
      toast.error('Failed to add comment');
    }
  };

  const getPageComments = async () => {
    try {
      const response = await httpInjectorService.getPageComment(
        spaceid,
        pageid,
        type,
      );

      if (response?.status === 'success') {
        const flattenComments = (comments) => {
          let result = [];

          comments.forEach((c) => {
            result.push({
              id: c.id,
              parentId: c.parent_comment_id,
              user: c.creator_name || 'User',
              date: formatDate(c.created_at),
              text: c.content,
            });

            if (c.replies && c.replies.length > 0) {
              result = result.concat(flattenComments(c.replies));
            }
          });

          return result;
        };

        const formattedComments = flattenComments(response.data || []);

        setComments(formattedComments);
      }
    } catch (error) {
      console.error(error);
      toast.error('Failed to load comments');
    }
  };

  const handleDelete = async (id) => {
    try {
      const response = await httpInjectorService.deletePageComment(
        spaceid,
        pageid,
        id,
      );

      if (response?.status === 'success') {
        getPageComments();
        toast.success('Comment deleted');
      }
    } catch (error) {
      console.error(error);
      toast.error('Failed to delete comment');
    }
  };

  const startEdit = (comment) => {
    setEditingId(comment.id);
    setEditText(comment.text);
  };

  const saveEdit = async (id) => {
    try {
      const payload = {
        id: id,
        space_id: spaceid,
        item_id: pageid,
        content: editText,
      };

      const response = await httpInjectorService.editPageComment(payload);

      if (response?.status === 'success') {
        getPageComments();
        // toast.success("Comment updated");
      }

      setEditingId(null);
      setEditText('');
    } catch (error) {
      console.error(error);
      toast.error('Failed to update comment');
    }
  };

  const renderReplies = (parentId) => {
    return comments
      .filter((c) => c.parentId === parentId)
      .map((c) => (
        <div
          key={c.id}
          style={{
            marginTop: 16,
            display: 'flex',
            gap: 10,
          }}
        >
          <UserAvatar username={c.user} />

          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600 }}>{c.user}</div>

            <div style={{ fontSize: 12, color: '#888' }}>{c.date}</div>

            {editingId === c.id ? (
              <>
                <ReactQuill
                  key={`edit-${c.id}`}
                  value={editText}
                  onChange={setEditText}
                  modules={quillModules}
                />

                <div style={{ marginTop: 8 }}>
                  <Button
                    onClick={() => saveEdit(c.id)}
                    style={{ background: '#722ed1', color: '#fff' }}
                  >
                    Save
                  </Button>

                  <Button
                    style={{ marginLeft: 8 }}
                    onClick={() => setEditingId(null)}
                  >
                    Cancel
                  </Button>
                </div>
              </>
            ) : (
              <>
                <div
                  dangerouslySetInnerHTML={{ __html: c.text }}
                  style={{ marginTop: 4 }}
                />

                <div
                  style={{
                    display: 'flex',
                    gap: 14,
                    marginTop: 6,
                    fontSize: 13,
                  }}
                >
                  <RollbackOutlined
                    style={{ cursor: 'pointer' }}
                    onClick={() => setReplyParent(c.id)}
                  />

                  <EditOutlined
                    style={{ cursor: 'pointer' }}
                    onClick={() => startEdit(c)}
                  />

                  <DeleteOutlined
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleDelete(c.id)}
                  />
                </div>
              </>
            )}

            {replyParent === c.id && (
              <div style={{ marginTop: 10 }}>
                <ReactQuill
                  value={replyText}
                  onChange={setReplyText}
                  modules={quillModules}
                />

                <div style={{ marginTop: 8 }}>
                  <Button
                    onClick={() => handleAddComment(c.id)}
                    style={{ background: '#722ed1', color: '#fff' }}
                  >
                    Add
                  </Button>

                  <Button
                    style={{ marginLeft: 8 }}
                    onClick={() => setReplyParent(null)}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
            {renderReplies(c.id)}
          </div>
        </div>
      ));
  };

  return (
    <div
      style={
        isPanel
          ? {}
          : {
              borderTop: '1px solid #eee',
              marginTop: 40,
              paddingTop: 20,
            }
      }
    >
      {type === 'page' && !showBox && (
        <Button type="dashed" onClick={() => setShowBox(true)}>
          <CommentOutlined /> Add Comment
        </Button>
      )}

      {showBox && (
        <div style={{ marginTop: 12 }}>
          <ReactQuill
            key="main-comment-editor"
            theme="snow"
            value={comment}
            onChange={setComment}
            placeholder="Write a comment..."
            modules={quillModules}
          />

          <div style={{ marginTop: 10 }}>
            <Button
              onClick={() => handleAddComment(null)}
              style={{ backgroundColor: '#722ed1', color: 'white' }}
            >
              Add
            </Button>

            <Button
              style={{ marginLeft: 8 }}
              onClick={() => {
                setShowBox(false);
                setComment('');
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
      <div style={{ marginTop: 30 }}>{renderReplies(null)}</div>
    </div>
  );
};

export default PageComments;
